/**
 * Permission utility functions
 * Used to check user permissions and filter data based on roles and shop access
 */

/**
 * Check if user has ADMIN role
 * @param {Object} user - User object from auth state
 * @returns {boolean} - True if user has ADMIN role
 */
export const isAdmin = (user) => {
  if (!user || !user.roles) return false;
  return user.roles.includes('ADMIN') || user.roles.includes('SUPERADMIN');
};

/**
 * Check if user has access to a specific shop
 * @param {Object} user - User object from auth state
 * @param {string} shopId - Shop ID to check access for
 * @returns {boolean} - True if user has access to the shop
 */
export const hasShopAccess = (user, shopId) => {
  if (isAdmin(user)) return true;
  return user?.shopIds?.includes(shopId) || false;
};

/**
 * Get all shop IDs user has access to
 * @param {Object} user - User object from auth state
 * @returns {string|Array} - 'all' for admins, array of shopIds for others
 */
export const getAccessibleShopIds = (user) => {
  if (isAdmin(user)) return 'all';
  return user?.shopIds || [];
};

/**
 * Filter array by shop access
 * @param {Array} items - Array of items to filter
 * @param {Object} user - User object from auth state
 * @param {Function} getShopId - Function to extract shopId(s) from item
 * @returns {Array} - Filtered array
 */
export const filterByShopAccess = (items, user, getShopId) => {
  if (isAdmin(user)) return items;

  const accessibleShopIds = user?.shopIds || [];
  return items.filter(item => {
    const shopId = getShopId(item);

    // Handle single shopId
    if (typeof shopId === 'string') {
      return accessibleShopIds.includes(shopId);
    }

    // Handle array of shopIds
    if (Array.isArray(shopId)) {
      return shopId.some(id => accessibleShopIds.includes(id));
    }

    return false;
  });
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - User object from auth state
 * @param {Array<string>} roles - Array of role names to check
 * @returns {boolean} - True if user has at least one of the roles
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(roles)) return false;
  return roles.some(role => user.roles.includes(role));
};

/**
 * Check if user can view a sidebar menu based on role
 * @param {Object} user - User object from auth state
 * @param {string} menuLabel - Menu label/identifier
 * @returns {boolean} - True if user can view the menu
 */
export const canViewMenu = (user, menuLabel) => {
  if (!user || !user.roles) return false;

  const userRoles = user.roles;

  // ADMIN: Can see everything
  if (userRoles.includes('SUPERADMIN')) return true;

  // PRODUCTION MANAGER: Can see ONLY Inventaire + Stock & Production
  if (userRoles.includes('PRODUCTION MANAGER')) {
    return [
      'Inventaire', 'Périodes', 'Produits', 'Nouveau produit',
      'Familles', 'Sous familles', 'Unités',
      'Stock & Production', 'Gestion Stock', 'A transformer',
      'Poubelle', 'Stock négatif', 'Ordres de fabrication', 'Transferts',
      'Achats', 'Nouvel achat'
    ].includes(menuLabel);
  }

  // MANAGER and ASSISTANT MANAGER: Can see everything EXCEPT Paramètres de société and dashboards temporally
  if (userRoles.includes('MANAGER') || userRoles.includes('ASSISTANT MANAGER')) {
    return menuLabel !== 'Principale';
  }

  // Default: Don't allow access erp to sellers
  if (userRoles.includes('SELLER')) {
    return false;
  }


  if (userRoles.includes('ADMIN')) {
    return menuLabel !== 'Dashboard';
  }

  // Default: Don't allow access
  return false;
};

/**
 * Check if user has a specific role or can see content restricted to that role
 * @param {Array<string>} userRoles - Array of user roles from auth state (user.roles)
 * @param {string} targetRole - Target role to check (e.g., 'ADMIN')
 * @returns {boolean} - True if user has the target role
 */
export const canSee = (userRoles, targetRole) => {
  if (!userRoles || !Array.isArray(userRoles)) return false;
  if (!targetRole) return false;
  return userRoles.includes(targetRole);
};


/**
 * Détermine le shopId cible et construit une route en fonction du nombre de shops
 * @param {Object} user - user depuis Redux (avec shopIds)
 * @param {string} basePath - chemin sans shopId, ex: 'admin-dashboard'
 * @returns {string} - route complète ex: '/all/admin-dashboard' ou '/shop123/admin-dashboard'
 */
export const getRouteByShopContext = (user, basePath) => {
  const shopIds = user?.shopIds || [];

  if (shopIds.length === 1) {
    const onlyShopId = shopIds[0];
    return `/${onlyShopId}/${basePath}`;
  }

  // 0 ou plusieurs shops → vue globale
  return `/all/${basePath}`;
};